import { CBasketConfigurationKeys } from '@epicuro-next/data/baskets';
import { totalToDiscount } from '@epicuro-next/data/baskets-grouping';
import { isNumber, isString } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { BasketItem, OrderType } from 'graphql-api/baskets-api-interfaces';

import { productIdMatchesPredicate } from '../../shared';
import { BasketItemCalculationParametersForNetInputArgs } from '../../shared/basket-manual-discount-calculation.utils';
import { ManualDiscountPercentageEntry } from '../../types';
import { ManualDiscountEntity } from '../reducers/basket-manual-discounts.reducer';

import {
  selectManualDiscountValidatonRuleAllowValueBelowCDiscount,
  selectManualDiscountValidatonRuleAllowedItems,
  selectMaxDiscountPercentage,
} from './basket-configuration-rules.selectors';
import {
  getTotalForEachBasketItem,
  getTotalsForBasketItemIdsFromDtoBasket,
  selectBasketItemIds,
  selectBasketItems,
  selectBasketItemsWithReferenceToClosedOutProducts,
  selectDiscountPercentageByBasketLineId,
  selectFutureBasketItems,
  selectMaterialIdForBasketItems,
  selectNowBasketItems,
  selectOpenedBasketId,
  selectOpenedDtoBasketFromEntity,
} from './basket-data.selectors';
import { selectBasketManualDiscountsState } from './feature.selectors';

type ManualDiscountsPercentageEntryFromConfig = {
  materialId: unknown;
  maxDiscountPercentage: unknown;
  orderType: unknown;
};

export const selectBasketManualDiscounts = createSelector(
  selectBasketManualDiscountsState,
  selectOpenedBasketId,
  (state, openedBasketId) => {
    return Object.values(state.entities)
      .filter((e) => !!e && e.basketId === openedBasketId)
      .map((e) => e as ManualDiscountEntity);
  },
);

export const selectAllBasketManualDiscounts = createSelector(
  selectBasketManualDiscountsState,
  (state) => {
    return Object.values(state.entities) as ManualDiscountEntity[];
  },
);

export const selectBasketManualDiscountBasketItemIds = createSelector(
  selectBasketManualDiscounts,
  (manualDiscounts) => {
    return manualDiscounts.map((e) => e.basketItemId);
  },
);

export const selectBasketManualDiscountValues = createSelector(
  selectBasketManualDiscounts,
  (manualDiscounts) =>
    manualDiscounts
      .filter((e) => e.currentValue !== null)
      .map((e) => e.currentValue as number),
);

export const selectHasSomeBasketManualDiscount = createSelector(
  selectBasketManualDiscounts,
  (manualDiscountValues) =>
    manualDiscountValues.some(
      (manualDiscount) => manualDiscount.currentValue !== null,
    ),
);

export const selectBasketItemsEligibleForManualDiscount = createSelector(
  selectBasketItemsWithReferenceToClosedOutProducts,
  selectFutureBasketItems,
  selectNowBasketItems,
  selectManualDiscountValidatonRuleAllowedItems,
  (
    basketItemsWithReferenceToClosedOutProducts,
    futureBasketItems,
    nowBasketItems,
    allowedItems,
  ) => {
    const set = new Set<string>();
    const basketItems: BasketItem[] = [];
    [
      ...(allowedItems.includes('closedout')
        ? basketItemsWithReferenceToClosedOutProducts ?? []
        : []),
      ...(allowedItems.includes('preorder') ? futureBasketItems ?? [] : []),
      ...(allowedItems.includes('reorder') ? nowBasketItems ?? [] : []),
    ].forEach((item) => {
      if (!set.has(item.basketItemId)) {
        set.add(item.basketItemId);
        basketItems.push(item);
      }
    });
    return basketItems;
  },
);

export type DiscountOrigin = 'manual' | 'calculated';

export const selectBasketManualDiscountValuesOrCalculatedDiscountForEligibleItems =
  createSelector(
    selectBasketItemsEligibleForManualDiscount,
    selectBasketManualDiscounts,
    selectDiscountPercentageByBasketLineId,
    (
      eligibleBasketItems,
      manualDiscountValues,
      discountPercentageByBasketLineId,
    ) => {
      const discountValues: {
        [basketItemId: string]: {
          discount?: number;
          origin: DiscountOrigin;
        };
      } = {};

      eligibleBasketItems.forEach((basketItem) => {
        const basketItemId = basketItem.basketItemId;
        const manualDiscount = manualDiscountValues.find(
          (e) => e.basketItemId === basketItemId,
        );

        let origin: DiscountOrigin;
        let discount: number | undefined;

        if (!!manualDiscount && manualDiscount.currentValue !== null) {
          discount = manualDiscount.currentValue;
          origin = 'manual';
        } else {
          discount = discountPercentageByBasketLineId[basketItemId];
          origin = 'calculated';
        }

        discountValues[basketItemId] = {
          discount,
          origin,
        };
      });

      return discountValues;
    },
  );

export const selectBasketItemIdsEligibleForManualDiscount = createSelector(
  selectBasketItemsEligibleForManualDiscount,
  (items) => items.map(({ basketItemId }) => basketItemId),
);

export const selectHasBasketItemsEligibleForManualDiscount = createSelector(
  selectBasketItemsEligibleForManualDiscount,
  (basketItemsEligibleForManualDiscount) =>
    (basketItemsEligibleForManualDiscount?.length ?? 0) > 0,
);

export const selectBasketItemsIneligibleForManualDiscount = createSelector(
  selectBasketItems,
  selectBasketItemIdsEligibleForManualDiscount,
  (basketItems, eligibleBasketItemIds) =>
    basketItems.filter(
      (item) => !eligibleBasketItemIds.includes(item.basketItemId),
    ),
);

export const selectBasketItemIdsIneligibleForManualDiscount = createSelector(
  selectBasketItemsIneligibleForManualDiscount,
  (items) => items.map(({ basketItemId }) => basketItemId),
);

export const selectTotalOfBasketItemsIneligibleForManualDiscount =
  createSelector(
    selectOpenedDtoBasketFromEntity,
    selectBasketItemIdsIneligibleForManualDiscount,
    (openedDtoBasket, basketItemIds) => {
      if (!openedDtoBasket) {
        return undefined;
      }

      const basketItems = openedDtoBasket.items.filter((item) =>
        basketItemIds.includes(item.itemId),
      );

      return getTotalsForBasketItemIdsFromDtoBasket(basketItems);
    },
  );

export const selectTotalNetOfBasketItemsIneligibleForManualDiscount =
  createSelector(
    selectTotalOfBasketItemsIneligibleForManualDiscount,
    (ineligibleItemsTotal) => ineligibleItemsTotal?.confirmed?.net ?? 0,
  );

export const selectTotalGrossOfBasketItemsIneligibleForManualDiscount =
  createSelector(
    selectTotalOfBasketItemsIneligibleForManualDiscount,
    (ineligibleItemsTotal) => ineligibleItemsTotal?.confirmed?.wsp ?? 0,
  );

export const selectTotalsOfBasketItemsEligibleForManualDiscount =
  createSelector(
    selectOpenedDtoBasketFromEntity,
    selectBasketItemIdsEligibleForManualDiscount,
    (openedDtoBasket, eligibleBasketItemIds) => {
      return getTotalForEachBasketItem(openedDtoBasket, eligibleBasketItemIds);
    },
  );

export const selectTotalsOfBasketItemsIneligibleForManualDiscount =
  createSelector(
    selectOpenedDtoBasketFromEntity,
    selectBasketItemIdsIneligibleForManualDiscount,
    (openedDtoBasket, eligibleBasketItemIds) => {
      return getTotalForEachBasketItem(openedDtoBasket, eligibleBasketItemIds);
    },
  );

export const selectMaximumAllowedValueForBasketItemsEligibleForManualDiscount =
  createSelector(
    selectBasketItemsEligibleForManualDiscount,
    selectTotalsOfBasketItemsEligibleForManualDiscount,
    selectManualDiscountValidatonRuleAllowValueBelowCDiscount,
    (
      basketItemsEligibleForManualDiscount,
      totalsOfBasketItemsEligibleForManualDiscount,
      allowValueBelowCalculatedDiscount,
    ) => {
      const allowValueBelowCalculatedDiscountByOrderType = {
        [OrderType.Now.toString()]:
          allowValueBelowCalculatedDiscount.reOrder ||
          allowValueBelowCalculatedDiscount.closedOut,
        [OrderType.Future.toString()]:
          allowValueBelowCalculatedDiscount.preOrder,
      };

      const maxValues: { [basketItemId: string]: number } = {};

      basketItemsEligibleForManualDiscount.forEach((basketItem) => {
        const total =
          totalsOfBasketItemsEligibleForManualDiscount[basketItem.basketItemId];

        let maxValueToUse: number | undefined = 0;
        const orderTypeStr = basketItem.orderType.toString();
        if (allowValueBelowCalculatedDiscountByOrderType[orderTypeStr]) {
          maxValueToUse = total?.confirmed?.wsp;
        } else if (!!total && total.confirmed?.wsp !== undefined) {
          const useRrpForDiscount = false;
          const discount = totalToDiscount(total, useRrpForDiscount);
          maxValueToUse = total.confirmed.wsp - Math.abs(discount.value);
        }

        maxValues[basketItem.basketItemId] =
          maxValueToUse !== undefined ? maxValueToUse : 0;
      });

      return maxValues;
    },
  );

export const selectManualDiscountsPercentageEntriesString = createSelector(
  selectBasketManualDiscountsState,
  (state) => {
    // Turns it into a string in case every we might want to go back to JSON structures in rule engine instead of conditions for this kind of configuration.
    // Because in FRT code that would make it much simpler, see https://epicuro.atlassian.net/browse/FRT-2313?focusedCommentId=154352
    const manualDiscountsPercentageEntries: ManualDiscountPercentageEntry[] =
      [];
    Object.entries(state.maxDiscountPercentagesForProducts).forEach(
      ([orderType, discountByProductId]) => {
        Object.entries(discountByProductId).forEach(
          ([productId, maxDiscountPercentage]) => {
            manualDiscountsPercentageEntries.push({
              maxDiscountPercentage,
              orderType: orderType as OrderType,
              materialId: productId,
            });
          },
        );
      },
    );

    return JSON.stringify(manualDiscountsPercentageEntries);
  },
);

export const selectManualDiscountsPercentageEntries = createSelector(
  selectManualDiscountsPercentageEntriesString,
  (manualDiscountsPercentageEntriesString) => {
    try {
      let manualDiscountPercentageEntriesFromConfig = JSON.parse(
        manualDiscountsPercentageEntriesString ?? '[]',
      ) as
        | ManualDiscountsPercentageEntryFromConfig
        | ManualDiscountsPercentageEntryFromConfig[];

      if (!Array.isArray(manualDiscountPercentageEntriesFromConfig)) {
        manualDiscountPercentageEntriesFromConfig = [
          manualDiscountPercentageEntriesFromConfig,
        ];
      }

      const manualDiscountPercentageEntries: ManualDiscountPercentageEntry[] =
        [];

      manualDiscountPercentageEntriesFromConfig
        .filter((entry) => !!entry)
        .forEach((entry) => {
          const styleId = getMaterialIdFrom(entry);
          const existingOrderType = getOrderTypeFrom(entry);
          const maxDiscountPercentage = getMaxDiscountPercentage(entry);

          const validStyleId = !!styleId;
          if (
            !validStyleId ||
            !existingOrderType ||
            isNaN(maxDiscountPercentage) ||
            !isNumber(maxDiscountPercentage) ||
            maxDiscountPercentage < 0 ||
            maxDiscountPercentage > 1
          ) {
            console.warn(
              `Config value "${JSON.stringify(entry)}" is not valid.`,
              { validStyleId, existingOrderType, maxDiscountPercentage },
            );
          } else {
            manualDiscountPercentageEntries.push({
              materialId: styleId,
              maxDiscountPercentage,
              orderType: existingOrderType,
            });
          }
        });

      return manualDiscountPercentageEntries;
    } catch (e) {
      console.warn(`Parsing rule for manual discounts percentage entries failed.
          Value of rule '${CBasketConfigurationKeys.ManualDiscountPercentageEntries}'. Exception message: ${e.message}}`);
      return [];
    }
  },
);

export const selectBasketManualDiscountMaxPercentageForEligibleItems =
  createSelector(
    selectBasketItemsEligibleForManualDiscount,
    selectManualDiscountsPercentageEntries,
    selectMaxDiscountPercentage,
    selectDiscountPercentageByBasketLineId,
    (
      eligibleBasketItems,
      percentageEntries,
      maxDiscountPercentage,
      calculatedDiscounts,
    ) => {
      const maxDiscountPercentages: {
        [basketItemId: string]: number;
      } = {};

      const initial: { [key in keyof typeof maxDiscountPercentage]: boolean } =
        {
          FUTURE: false,
          NOW: false,
        };

      const userIsAllowedToGiveDiscounts = Object.entries(
        maxDiscountPercentage,
      ).reduce((acc, [key, value]) => {
        acc[key as keyof typeof maxDiscountPercentage] = value > 0;
        return acc;
      }, initial);

      eligibleBasketItems.forEach((basketItem) => {
        const basketItemId = basketItem.basketItemId;

        const percentageEntry = percentageEntries.find((pe) => {
          return (
            productIdMatchesPredicate(
              basketItem.materialId ?? '',
              pe.materialId,
            ) &&
            (pe.orderType === OrderType.Mixed ||
              pe.orderType === basketItem.orderType)
          );
        });

        function isApplicableOrderType(
          value: OrderType,
        ): value is OrderType.Future | OrderType.Now {
          return value === OrderType.Future || value === OrderType.Now;
        }

        const orderType = basketItem.orderType;
        if (
          isApplicableOrderType(orderType) &&
          userIsAllowedToGiveDiscounts[orderType]
        ) {
          if (percentageEntry !== undefined) {
            maxDiscountPercentages[basketItemId] =
              percentageEntry.maxDiscountPercentage;
          } else {
            maxDiscountPercentages[basketItemId] =
              maxDiscountPercentage[orderType];
          }
          const calculatedDiscount = calculatedDiscounts[basketItemId];

          if (
            calculatedDiscount !== undefined &&
            maxDiscountPercentages[basketItemId] < calculatedDiscount
          ) {
            maxDiscountPercentages[basketItemId] =
              Math.round(calculatedDiscount * 10000) / 10000;
          }
        } else {
          maxDiscountPercentages[basketItemId] = 0;
        }
      });

      return maxDiscountPercentages;
    },
  );

export const selectMinimumAllowedValueForBasketItemsEligibleForManualDiscount =
  createSelector(
    selectTotalsOfBasketItemsEligibleForManualDiscount,
    selectBasketManualDiscountMaxPercentageForEligibleItems,
    (
      totalsOfBasketItemsEligibleForManualDiscount,
      basketManualDiscountMaxPercentageForEligibleItems,
    ) => {
      const minValues: { [basketItemId: string]: number } = {};

      Object.keys(basketManualDiscountMaxPercentageForEligibleItems).forEach(
        (basketItemId) => {
          const total =
            totalsOfBasketItemsEligibleForManualDiscount[basketItemId];

          const maxPercentage =
            basketManualDiscountMaxPercentageForEligibleItems[basketItemId];

          const minValue = (total?.confirmed?.wsp ?? 0) * (1 - maxPercentage);
          minValues[basketItemId] = minValue;
        },
      );

      return minValues;
    },
  );

export const selectAllowedValueRangeForBasketItemsEligibleForManualDiscount =
  createSelector(
    selectMinimumAllowedValueForBasketItemsEligibleForManualDiscount,
    selectMaximumAllowedValueForBasketItemsEligibleForManualDiscount,
    (minValuesAllowed, maxValuesAllowed) => {
      const minMaxCombined: {
        [basketItemId: string]: {
          minValue: number;
          maxValue: number;
        };
      } = {};

      if (!!minValuesAllowed && !!maxValuesAllowed) {
        Object.keys(minValuesAllowed).forEach((basketItemId) => {
          minMaxCombined[basketItemId] = {
            minValue: minValuesAllowed[basketItemId],
            maxValue: maxValuesAllowed[basketItemId],
          };
        });
      }

      return minMaxCombined;
    },
  );

export const selectMinimumAllowedValueForTotalNet = createSelector(
  selectTotalNetOfBasketItemsIneligibleForManualDiscount,
  selectMinimumAllowedValueForBasketItemsEligibleForManualDiscount,
  (
    totalNetOfBasketItemsIneligibleForManualDiscount,
    minimumAllowedValueForBasketItemsEligibleForManualDiscount,
  ) => {
    const sumOfMinValuesOfBasketItemsEligibleForManualDiscount = Object.values(
      minimumAllowedValueForBasketItemsEligibleForManualDiscount,
    ).reduce((acc, val) => acc + val, 0);

    const minimumAllowedValueForTotalNet: number =
      totalNetOfBasketItemsIneligibleForManualDiscount +
      sumOfMinValuesOfBasketItemsEligibleForManualDiscount;

    return minimumAllowedValueForTotalNet;
  },
);

export const selectMaximumAllowedValueForTotalNet = createSelector(
  selectTotalNetOfBasketItemsIneligibleForManualDiscount,
  selectMaximumAllowedValueForBasketItemsEligibleForManualDiscount,
  (
    totalNetOfBasketItemsIneligibleForManualDiscount,
    maximumAllowedValueForBasketItemsEligibleForManualDiscount,
  ) => {
    const totalMaxAllowedValueForBasketItemsEligibleForManualDiscount =
      Object.values(
        maximumAllowedValueForBasketItemsEligibleForManualDiscount,
      ).reduce((acc, val) => acc + val, 0);

    const maximumAllowedValueForTotalNet =
      totalNetOfBasketItemsIneligibleForManualDiscount +
      totalMaxAllowedValueForBasketItemsEligibleForManualDiscount;

    return maximumAllowedValueForTotalNet;
  },
);

export const selectAllowedRangeForTotalNet = createSelector(
  selectMinimumAllowedValueForTotalNet,
  selectMaximumAllowedValueForTotalNet,
  (min, max) => {
    const range = { min, max };
    return range;
  },
);

export const selectBasketItemsCalculationParametersForNetInputArgs =
  createSelector(
    selectBasketItemIds,
    selectAllowedValueRangeForBasketItemsEligibleForManualDiscount,
    selectTotalsOfBasketItemsIneligibleForManualDiscount,
    selectTotalsOfBasketItemsEligibleForManualDiscount,
    (
      allBasketItemIds,
      allowedValueRangeForBasketItemsEligibleForManualDiscount,
      totalsOfBasketItemsIneligibleForManualDiscount,
      totalsOfBasketItemsEligibleForManualDiscount,
    ) => {
      const basketItemsInputArgs: {
        [basketItemId: string]: BasketItemCalculationParametersForNetInputArgs;
      } = {};

      allBasketItemIds.forEach((basketItemId) => {
        const allowedValueRangeForBasketItem =
          allowedValueRangeForBasketItemsEligibleForManualDiscount[
            basketItemId
          ];
        const isEligible = !!allowedValueRangeForBasketItem;

        let absoluteAllowedRangeForDiscount:
          | number
          | {
              min: number;
              max: number;
            };

        let gross: number;
        if (isEligible) {
          absoluteAllowedRangeForDiscount = {
            min: allowedValueRangeForBasketItem.minValue,
            max: allowedValueRangeForBasketItem.maxValue,
          };

          gross =
            totalsOfBasketItemsEligibleForManualDiscount[basketItemId]
              ?.confirmed?.wsp ?? 0;
        } else {
          const totalOfIneligibleBasketItem =
            totalsOfBasketItemsIneligibleForManualDiscount[basketItemId];

          const absoluteDiscountValue = totalToDiscount(
            totalOfIneligibleBasketItem,
            false,
          ).value;
          gross = totalOfIneligibleBasketItem.confirmed?.wsp ?? 0;
          absoluteAllowedRangeForDiscount =
            gross - Math.abs(absoluteDiscountValue);
        }

        basketItemsInputArgs[basketItemId] = {
          absoluteAllowedRangeForDiscount,
          gross,
        };
      });

      return basketItemsInputArgs;
    },
  );

export const selectMaterialIdForBasketItemIdsEligibleForManualDiscount =
  createSelector(
    selectMaterialIdForBasketItems,
    selectBasketItemIdsEligibleForManualDiscount,
    (materialIdForBasketItems, eligibleBasketItemIds) => {
      const materialIdForBasketItemsEligibleForManualDiscount: {
        [basketItemId: string]: string;
      } = {};

      eligibleBasketItemIds.forEach((basketItemId) => {
        materialIdForBasketItemsEligibleForManualDiscount[basketItemId] =
          materialIdForBasketItems[basketItemId];
      });

      return materialIdForBasketItemsEligibleForManualDiscount;
    },
  );

export const selectOrderTypeForBasketItemIdsEligibleForManualDiscount =
  createSelector(
    selectBasketItemsEligibleForManualDiscount,
    (basketItemsEligibleForManualDiscount) => {
      const orderTypeForBasketItemsEligibleForManualDiscount: {
        [basketItemId: string]: OrderType;
      } = {};

      basketItemsEligibleForManualDiscount.forEach((basketItem) => {
        orderTypeForBasketItemsEligibleForManualDiscount[
          basketItem.basketItemId
        ] = basketItem.orderType;
      });

      return orderTypeForBasketItemsEligibleForManualDiscount;
    },
  );

function getMaxDiscountPercentage(entry: { maxDiscountPercentage: unknown }) {
  let maxDiscountPercentage = Number(entry.maxDiscountPercentage);

  if (isNaN(maxDiscountPercentage)) {
    maxDiscountPercentage = 0;
  }

  if (maxDiscountPercentage > 1) {
    return maxDiscountPercentage / 100;
  }
  return maxDiscountPercentage;
}

function getOrderTypeFrom(entry: { orderType: unknown }) {
  const orderTypeOnEntry = isString(entry.orderType)
    ? entry.orderType.toUpperCase()
    : '';

  const existingOrderType = Object.values(OrderType).find(
    (value) => orderTypeOnEntry === value.toString().toUpperCase(),
  );

  return existingOrderType;
}

function getMaterialIdFrom(entry: {
  materialId: unknown;
  maxDiscountPercentage: unknown;
  orderType: unknown;
}): string | undefined {
  const styleId = entry.materialId;
  if (styleId) {
    if (isString(styleId)) {
      return styleId;
    } else if (isNumber(styleId)) {
      return styleId.toString();
    }
  }

  return undefined;
}
